import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { AlertTriangle, ChevronRight, CreditCard, TicketPercent } from 'lucide-react';

import { loadStorefrontView } from './api';
import { Empty } from './parts';
import { useLiveResource } from './useLive';
import { CHART, ChartTip, KpiRow, NUMBER, Panel, Share, pointLabel, tipPoint } from './MobileParts';
import {
  Funnel, RankRows, ReadState, StoreTable, StorefrontGate, StorefrontHead, codeText, count, duration, rate, ratio, st, stCount,
  useStorefrontSite,
} from './StorefrontParts';
import { stageLabel } from './StorefrontOverview';

// From a full bag to a paid order: how many get to each checkout step and how
// long the step before took them, which coupons they tried and why some were
// refused, how payments failed, and what went wrong on the way. The steps are
// whatever the store sends, in the order visitors reach them.

function CheckoutChart({ data }) {
  if (data.series.every((point) => point.checkouts === 0)) return <Empty title={st('checkout.no_checkouts')} />;
  return (
    <div className="xo-mob-chart">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data.series} margin={{ top: 8, right: 18, left: 0, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke={CHART.grid} />
          <XAxis dataKey="at" tickFormatter={pointLabel(data.step)} tickLine={false} axisLine={false} minTickGap={24} tick={CHART.tick} />
          <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={CHART.tick} width={32} />
          <Tooltip cursor={CHART.cursor} animationDuration={120} content={(
            <ChartTip title={tipPoint(data.step)}
              rows={(point) => [
                { name: st('checkout.series_checkouts'), color: CHART.pale, mark: 'bar', text: NUMBER.format(point.checkouts) },
                { name: st('checkout.series_payments'), color: CHART.mid, mark: 'line', text: NUMBER.format(point.payments) },
                { name: st('checkout.series_purchases'), color: CHART.ink, mark: 'line', text: NUMBER.format(point.purchases) },
              ]}
              facts={(point) => [point.checkouts > 0 ? { label: st('checkout.tip_completed'), value: rate(point.purchases, point.checkouts) } : null]} />
          )} />
          <Bar dataKey="checkouts" fill={CHART.pale} radius={[3, 3, 0, 0]} maxBarSize={22} name={st('checkout.series_checkouts')} />
          <Line type="monotone" dataKey="payments" stroke={CHART.mid} strokeWidth={1.8} dot={false} name={st('checkout.series_payments')} />
          <Line type="monotone" dataKey="purchases" stroke={CHART.ink} strokeWidth={2.2} dot={false} name={st('checkout.series_purchases')} />
        </ComposedChart>
      </ResponsiveContainer>
      <div className="xo-mob-key">
        <span><i style={{ background: CHART.pale }} />{st('checkout.series_checkouts')}</span>
        <span><i className="is-line" style={{ background: CHART.mid }} />{st('checkout.series_payments')}</span>
        <span><i className="is-line" />{st('checkout.series_purchases')}</span>
      </div>
    </div>
  );
}

function Steps({ steps }) {
  if (steps.length === 0) return <Empty title={st('checkout.no_steps')} detail={st('checkout.no_steps_detail')} />;
  const most = Math.max(1, ...steps.map((step) => step.sessions));
  return (
    <ol className="xo-sf-steps">
      {steps.map((step, index) => (
        <li key={step.step}>
          <span className="xo-sf-steps-index">{index + 1}</span>
          <span className="xo-sf-steps-main">
            <strong>{codeText('checkout_step', step.step)}</strong>
            <small>{step.medianMsBefore === null ? st('checkout.first_step') : st('checkout.time_before', { time: duration(step.medianMsBefore) })}</small>
          </span>
          {/* Pale for everyone who reached the step, navy for those who went on (all of them on the last step). */}
          <span className="xo-sf-steps-bar" aria-hidden="true">
            <i style={{ width: `${(step.sessions / most) * 100}%` }} />
            <b style={{ width: `${((step.next === null ? step.sessions : step.next) / most) * 100}%` }} />
          </span>
          <span className="xo-sf-steps-count"><strong>{count(step.sessions)}</strong><small>{stCount('sessions_count', step.sessions)}</small></span>
          <span className={`xo-sf-steps-drop${step.dropOff !== null && step.dropOff > 0.4 ? ' is-steep' : ''}`}>
            {step.dropOff === null ? st('checkout.last_step') : st('checkout.dropped', { rate: ratio(step.dropOff) })}
          </span>
        </li>
      ))}
    </ol>
  );
}

function Coupons({ coupons }) {
  const columns = [
    { key: 'code', label: st('checkout.col_code'), primary: true, sortValue: (row) => row.code, render: (row) => <strong className="xo-sf-coupon">{row.code}</strong> },
    { key: 'applied', label: st('checkout.col_applied'), align: 'end', sortValue: (row) => row.applied, render: (row) => count(row.applied) },
    { key: 'rejected', label: st('checkout.col_rejected'), align: 'end', sortValue: (row) => row.rejected, render: (row) => count(row.rejected) },
    {
      key: 'reasons', label: st('checkout.col_reasons'), sortable: false,
      render: (row) => (row.reasons.length === 0 ? <span className="is-muted">{st('none')}</span> : (
        <span className="xo-sf-utm-chips">{row.reasons.map((reason) => (
          <em key={reason.reason || 'none'}>{reason.reason === null ? st('checkout.no_reason') : codeText('coupon_reason', reason.reason)} × {reason.count}</em>
        ))}</span>
      )),
    },
  ];
  return (
    <>
      <dl className="xo-sf-mini-facts">
        <div><dt>{st('checkout.coupon_attempts')}</dt><dd>{count(coupons.attempts)}</dd></div>
        <div><dt>{st('checkout.col_applied')}</dt><dd>{count(coupons.applied)}</dd></div>
        <div><dt>{st('checkout.col_rejected')}</dt><dd>{count(coupons.rejected)}</dd></div>
        <div><dt>{st('checkout.coupon_success')}</dt><dd>{rate(coupons.applied, coupons.attempts)}</dd></div>
      </dl>
      <StoreTable columns={columns} rows={coupons.codes} rowKey={(row) => row.code} limit={8} initialSort={{ key: 'applied', direction: 'desc' }}
        empty={<Empty title={st('checkout.no_coupons')} />} />
    </>
  );
}

function CheckoutView({ site, store }) {
  const [days, setDays] = useState(30);
  const load = useCallback(() => loadStorefrontView('checkout', { site: site.code, days }), [site.code, days]);
  const report = useLiveResource(['store', 'store_traffic'], load, { reconcileMs: 120000 });
  const head = <StorefrontHead title={st('checkout.title')} subtitle={st('checkout.subtitle')} days={days} onDays={setDays} store={store} />;
  if (report.data === null) return <div className="xo-platform-page xo-mobile-page xo-sf-page">{head}<ReadState resource={report} label={st('checkout.loading')} /></div>;
  const data = report.data;
  const { abandonment, payments } = data;
  return (
    <div className="xo-platform-page xo-mobile-page xo-sf-page">
      {head}
      {report.error && <p className="xo-alert t-warn" role="alert">{report.error}</p>}
      <KpiRow items={[
        { label: st('checkout.kpi_started'), value: count(abandonment.checkoutSessions), detail: st('checkout.kpi_started_detail'), trend: data.series.map((point) => point.checkouts) },
        { label: st('checkout.kpi_paid'), value: count(abandonment.purchasedSessions), detail: st('checkout.kpi_paid_detail', { rate: rate(abandonment.purchasedSessions, abandonment.checkoutSessions) }), trend: data.series.map((point) => point.purchases) },
        { label: st('checkout.kpi_abandoned'), value: ratio(abandonment.rate), detail: st('checkout.kpi_abandoned_detail', { count: NUMBER.format(Math.max(0, abandonment.checkoutSessions - abandonment.purchasedSessions)) }) },
        { label: st('checkout.kpi_failed'), value: count(payments.failed), detail: st('checkout.kpi_failed_detail', { rate: rate(payments.failed, payments.started) }) },
      ]} />
      <div className="xo-mob-row is-wide-left is-even">
        <Panel title={st('checkout.funnel_title')} detail={st('checkout.funnel_detail')}>
          <Funnel steps={data.funnel.map((step) => ({ key: step.stage, label: stageLabel(step.stage), count: step.sessions }))} />
        </Panel>
        <Panel title={st('checkout.over_time')} detail={st('checkout.over_time_detail')}>
          <CheckoutChart data={data} />
        </Panel>
      </div>
      <Panel title={st('checkout.steps_title')} detail={st('checkout.steps_detail')}
        aside={<Link className="xo-mob-link" to="/xomics/store/sessions?checkout=true&converted=false">{st('checkout.abandoned_sessions')}<ChevronRight aria-hidden="true" /></Link>}>
        <Steps steps={data.steps} />
      </Panel>
      {/* Coupons on one side; failed payments and checkout errors, both short lists, stacked on the other. */}
      <div className="xo-mob-row is-halves is-even">
        <Panel title={st('checkout.coupons_title')} detail={st('checkout.coupons_detail')}>
          <Coupons coupons={data.coupons} />
        </Panel>
        <div className="xo-sf-stack">
          <Panel title={st('checkout.payments_title')} detail={st('checkout.payments_detail', { started: NUMBER.format(payments.started), failed: NUMBER.format(payments.failed) })}>
            <RankRows empty={<Empty title={st('checkout.no_failures')} />} rows={payments.failures.map((failure) => ({
              key: failure.code || 'unknown', icon: <CreditCard aria-hidden="true" />, label: failure.code ? codeText('decline', failure.code) : st('checkout.no_code'),
              title: failure.code || undefined, value: failure.count, display: NUMBER.format(failure.count),
            }))} />
          </Panel>
          <Panel title={st('checkout.errors_title')} detail={st('checkout.errors_plain')}>
            {data.errors.length === 0 ? <Empty title={st('checkout.no_errors')} /> : (
              <ol className="xo-mob-rank xo-sf-rank">
                {data.errors.map((error) => (
                  <li key={`${error.step}|${error.code}`}>
                    <span className="xo-mob-rank-icon xo-sf-bad-icon"><AlertTriangle aria-hidden="true" /></span>
                    <span className="xo-mob-rank-main">
                      <strong title={error.code || undefined}>{error.code ? codeText('checkout_error', error.code) : st('checkout.no_code')}</strong>
                      <small>{st('checkout.error_line', { step: codeText('checkout_step', error.step), sessions: stCount('sessions_count', error.sessions) })}</small>
                    </span>
                    <Share value={error.count} total={Math.max(1, ...data.errors.map((entry) => entry.count))} />
                    <em>{NUMBER.format(error.count)}</em>
                  </li>
                ))}
              </ol>
            )}
          </Panel>
        </div>
      </div>
      <p className="xo-mob-footnote"><TicketPercent aria-hidden="true" />{st('checkout.footnote')}</p>
    </div>
  );
}

export default function StorefrontCheckout() {
  const store = useStorefrontSite();
  return <StorefrontGate store={store}>{(site) => <CheckoutView site={site} store={store} />}</StorefrontGate>;
}
